import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, CirclePlus, ContactRound, Save, Trash2 } from 'lucide-react';
import {
  getRegistration,
  createRegistration,
  updateRegistration,
  deleteRegistration,
  getEventClasses,
  replaceClassEntries,
  getAppUsers,
  getAppUserRegistrationProfile,
} from '../services/api';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/shared/ConfirmDialog';
import type { Registration, EventClass, RegistrationClassEntry, AppUser } from '../types';
import { PageHeader, Alert, Button, Card, CardContent, CardHeader, CardTitle, Combobox, Field, Input, NumberInput, Separator, Spinner, Textarea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@racing/ui';

const REGISTRATION_STATUSES = ['PENDING', 'CONFIRMED', 'CHECKED_IN', 'CANCELLED', 'REFUNDED'];
const PAYMENT_STATUSES = ['UNPAID', 'PAID', 'REFUNDED'];
const PAYMENT_METHODS = ['CREDIT_CARD', 'PAYPAL', 'CASH', 'CHECK'];
const ENTRY_STATUSES = ['ACTIVE', 'WITHDRAWN', 'DQ'];

/** Radix Select has no empty-string value, so "none" needs a sentinel. */
const NONE = '__none__';

const humanise = (value: string) => value.replace(/_/g, ' ');

type TextFieldSpec = {
  key: keyof Registration;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date';
  required?: boolean;
  wide?: boolean;
};

const DRIVER_FIELDS: TextFieldSpec[] = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'occupation', label: 'Occupation' },
  { key: 'home_division', label: 'Home Division' },
  { key: 'license_number', label: 'License #' },
  { key: 'license_expiry', label: 'License Expiry', type: 'date' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
];

const ADDRESS_FIELDS: TextFieldSpec[] = [
  { key: 'street', label: 'Street', wide: true },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'Zip' },
];

const SELECTION_FIELDS: TextFieldSpec[] = [
  { key: 'event_selection', label: 'Event Selection' },
  { key: 'payment_selection', label: 'Payment Selection' },
  { key: 'raffle_selection', label: 'Raffle Selection' },
];

const PAYMENT_REF_FIELDS: TextFieldSpec[] = [
  { key: 'auth_code', label: 'Auth Code (Authorize.net)' },
  { key: 'transaction_id', label: 'Transaction ID' },
  { key: 'paypal_id', label: 'PayPal ID' },
];

const VEHICLE_FIELDS: Array<{ key: keyof RegistrationClassEntry; label: string }> = [
  { key: 'car_number', label: 'Car Number' },
  { key: 'category', label: 'Category' },
  { key: 'engine_make', label: 'Engine Make' },
  { key: 'engine_year', label: 'Engine Year' },
  { key: 'body_type', label: 'Body Type' },
  { key: 'body_year', label: 'Body Year' },
  { key: 'cui_cc', label: 'CUI/CC' },
  { key: 'hp', label: 'HP' },
  { key: 'factored_hp', label: 'Factored HP' },
];

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-4">
    <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
      {title}
    </h2>
    {children}
  </section>
);

const emptyEntry = (eventClassId: number): Partial<RegistrationClassEntry> => ({
  event_class_id: eventClassId,
  car_number: null,
  status: 'ACTIVE',
  category: null,
  engine_make: null,
  engine_year: null,
  body_type: null,
  body_year: null,
  cui_cc: null,
  hp: null,
  factored_hp: null,
});

const RegistrationDetailPage = () => {
  const { id: eventId, regId } = useParams<{ id: string; regId: string }>();
  const navigate = useNavigate();

  const parsedEventId = eventId ? Number(eventId) : 0;
  const isNew = !regId || regId === 'new';

  const [reg, setReg] = useState<Partial<Registration>>({
    status: 'PENDING',
    payment_status: 'UNPAID',
    amount_paid: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [eventClasses, setEventClasses] = useState<EventClass[]>([]);
  const [classEntries, setClassEntries] = useState<Partial<RegistrationClassEntry>[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [importSuccess, setImportSuccess] = useState('');

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const classes = await getEventClasses(parsedEventId);
      setEventClasses(classes);

      if (isNew) {
        try {
          setAppUsers(await getAppUsers());
        } catch {
          // Non-critical: the import shortcut just will not be offered.
        }
      }

      if (!isNew && regId) {
        const data = await getRegistration(parsedEventId, Number(regId));
        setReg(data);
        setClassEntries(data.class_entries || []);
      } else if (classes.length > 0) {
        setClassEntries([emptyEntry(classes[0].event_class_id)]);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [regId, parsedEventId, isNew]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleChange = (field: keyof Registration, value: string | number | boolean | null) =>
    setReg((prev) => ({ ...prev, [field]: value }));

  const handleEntryChange = (
    index: number,
    field: keyof RegistrationClassEntry,
    value: string | number | null
  ) =>
    setClassEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry))
    );

  const handleImportAppUser = async (appUserId: string) => {
    const user = appUsers.find((u) => String(u.id) === appUserId);
    if (!user) return;
    try {
      setImportSuccess('');
      const profile = await getAppUserRegistrationProfile(user.id);
      const { vehicles, ...profileFields } = profile;
      setReg((prev) => ({ ...prev, ...profileFields }));

      if (vehicles && vehicles.length > 0 && eventClasses.length > 0) {
        setClassEntries(
          vehicles.map((vehicle: Record<string, any>) => ({
            ...emptyEntry(eventClasses[0].event_class_id),
            car_number: vehicle.car_number || null,
            category: vehicle.category || null,
            engine_make: vehicle.engine_make || null,
            engine_year: vehicle.engine_year || null,
            body_type: vehicle.body_type || null,
            body_year: vehicle.body_year || null,
            cui_cc: vehicle.cui_cc || null,
            hp: vehicle.hp || null,
            factored_hp: vehicle.factored_hp || null,
          }))
        );
      }

      const displayName =
        [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || 'user';
      const vehicleCount = vehicles?.length || 0;
      setImportSuccess(
        `Loaded profile for ${displayName}${vehicleCount > 0 ? ` with ${vehicleCount} vehicle${vehicleCount === 1 ? '' : 's'}` : ''}`
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const created = await createRegistration(parsedEventId, reg);
        if (classEntries.length > 0) {
          await replaceClassEntries(parsedEventId, created.registration_id, classEntries);
        }
        setSuccess('Registration created');
        navigate(`/events/${parsedEventId}/registrations/${created.registration_id}`, {
          replace: true,
        });
      } else {
        await updateRegistration(parsedEventId, Number(regId), reg);
        await replaceClassEntries(parsedEventId, Number(regId), classEntries);
        setSuccess('Registration saved');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteRegistration(parsedEventId, Number(regId));
      navigate(`/events/${parsedEventId}`);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const renderTextField = ({ key, label, type = 'text', required, wide }: TextFieldSpec) => (
    <Field
      key={String(key)}
      label={label}
      required={required}
      className={wide ? 'sm:col-span-2 lg:col-span-4' : undefined}
    >
      {(p) => (
        <Input
          type={type}
          value={(reg[key] as string) || ''}
          onChange={(event) => handleChange(key, event.target.value)}
          {...p}
        />
      )}
    </Field>
  );

  if (!eventId) return <Navigate to="/events" replace />;

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2 self-start"
        onClick={() => navigate(`/events/${parsedEventId}`)}
      >
        <ArrowLeft /> Back to event
      </Button>

      <PageHeader
        title={isNew ? 'New Registration' : `Registration ${reg.registration_number ?? ''}`.trim()}
        actions={
          <>
            {!isNew && (
              <Button variant="destructive-outline" onClick={() => setConfirmOpen(true)}>
                <Trash2 /> Delete
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Spinner className="size-4 text-white" label={null} /> : <Save />}
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <div className="flex flex-col gap-6">
        {isNew && appUsers.length > 0 && (
          <div className="flex items-start gap-3 rounded-md border border-line bg-surface p-4">
            <ContactRound aria-hidden className="mt-1 size-5 shrink-0 text-accent-deep" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <h2 className="font-sans text-base font-semibold text-ink">Import from app user</h2>
              <Field
                label="Search app users by name or email"
                hint="Fills in their profile and vehicles. Every field stays editable before saving."
              >
                {(p) => (
                  <Combobox
                    id={p.id}
                    options={appUsers.map((user) => ({
                      value: String(user.id),
                      label:
                        [user.first_name, user.last_name].filter(Boolean).join(' ') +
                        (user.email ? ` (${user.email})` : ''),
                      keywords: [user.first_name, user.last_name, user.email]
                        .filter(Boolean)
                        .join(' '),
                    }))}
                    onSelect={handleImportAppUser}
                    placeholder="Select an app user"
                    searchPlaceholder="Name or email…"
                    emptyMessage="No matching app users"
                  />
                )}
              </Field>
              {importSuccess && (
                <Alert variant="success" onClose={() => setImportSuccess('')}>
                  {importSuccess}
                </Alert>
              )}
            </div>
          </div>
        )}

        <Section title="Driver information">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {DRIVER_FIELDS.map(renderTextField)}
            <Field label="Driver Bio" className="sm:col-span-2 lg:col-span-4">
              {(p) => (
                <Textarea
                  rows={3}
                  value={reg.driver_bio || ''}
                  onChange={(event) => handleChange('driver_bio', event.target.value)}
                  {...p}
                />
              )}
            </Field>
          </div>
        </Section>

        <Separator />

        <Section title="Address">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ADDRESS_FIELDS.map(renderTextField)}
          </div>
        </Section>

        <Separator />

        <Section title="Selections">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SELECTION_FIELDS.map(renderTextField)}
          </div>
        </Section>

        <Separator />

        <Section title="Payment">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Payment Method">
              {(p) => (
                <Select
                  value={reg.payment_method || NONE}
                  onValueChange={(value) =>
                    handleChange('payment_method', value === NONE ? null : value)
                  }
                >
                  <SelectTrigger id={p.id}>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {humanise(method)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Amount Paid">
              {(p) => (
                <NumberInput
                  value={reg.amount_paid ?? null}
                  onValueChange={(value) => handleChange('amount_paid', value ?? 0)}
                  nullable={false}
                  {...p}
                />
              )}
            </Field>

            <Field label="Payment Status">
              {(p) => (
                <Select
                  value={reg.payment_status || 'UNPAID'}
                  onValueChange={(value) => handleChange('payment_status', value)}
                >
                  <SelectTrigger id={p.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Registration Status">
              {(p) => (
                <Select
                  value={reg.status || 'PENDING'}
                  onValueChange={(value) => handleChange('status', value)}
                >
                  <SelectTrigger id={p.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGISTRATION_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {humanise(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {PAYMENT_REF_FIELDS.map(renderTextField)}
          </div>
        </Section>

        <Separator />

        <Section title="Car entries">
          <div className="flex flex-col gap-3">
            {classEntries.length === 0 && (
              <p className="rounded-md border border-dashed border-line-strong px-3 py-4 text-center font-sans text-sm text-ink-faint">
                No car entries. A registration needs at least one to appear in exports.
              </p>
            )}

            {classEntries.map((entry, index) => (
              // Index keys are correct here: entries have no id until saved and
              // add/remove rebuilds the array.
              <Card key={index}>
                <CardHeader className="flex-row items-center justify-between gap-2 pb-3">
                  <CardTitle className="text-base">
                    {entry.car_number ? `Car ${entry.car_number}` : `Entry ${index + 1}`}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove entry ${index + 1}`}
                    className="text-danger hover:bg-danger-bg"
                    onClick={() =>
                      setClassEntries((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <Trash2 />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Class" required>
                      {(p) => (
                        <Select
                          value={
                            entry.event_class_id ? String(entry.event_class_id) : ''
                          }
                          onValueChange={(value) =>
                            handleEntryChange(index, 'event_class_id', Number(value))
                          }
                        >
                          <SelectTrigger id={p.id}>
                            <SelectValue placeholder="Select a class" />
                          </SelectTrigger>
                          <SelectContent>
                            {eventClasses.map((eventClass) => (
                              <SelectItem
                                key={eventClass.event_class_id}
                                value={String(eventClass.event_class_id)}
                              >
                                {eventClass.class_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>

                    {VEHICLE_FIELDS.map(({ key, label }) => (
                      <Field key={String(key)} label={label}>
                        {(p) => (
                          <Input
                            value={(entry[key] as string) || ''}
                            onChange={(event) =>
                              handleEntryChange(index, key, event.target.value)
                            }
                            {...p}
                          />
                        )}
                      </Field>
                    ))}

                    <Field label="Entry Status">
                      {(p) => (
                        <Select
                          value={entry.status || 'ACTIVE'}
                          onValueChange={(value) => handleEntryChange(index, 'status', value)}
                        >
                          <SelectTrigger id={p.id}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTRY_STATUSES.map((status) => (
                              <SelectItem key={status} value={status}>
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div>
              <Button
                variant="outline"
                disabled={eventClasses.length === 0}
                onClick={() =>
                  setClassEntries((prev) => [
                    ...prev,
                    emptyEntry(eventClasses[0]?.event_class_id || 0),
                  ])
                }
              >
                <CirclePlus /> Add car entry
              </Button>
              {eventClasses.length === 0 && (
                <p className="mt-2 font-sans text-xs text-ink-faint">
                  This event has no classes defined yet, so entries cannot be added.
                </p>
              )}
            </div>
          </div>
        </Section>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete registration?"
        message="This removes the registration and its car entries from the event."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default RegistrationDetailPage;
